import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Index } from '@classes/index';
import { CompanyDetail } from '@classes/company-detail';
import { Contract } from '@classes/contract';
import { ContractConcession } from '@classes/contract-concession';
import { ContractConcessionDetail } from '@classes/contract-concession-detail';
import { ContractDetail } from '@classes/contract-detail';
import { ContractSlice } from '@classes/contract-slice';
import { ContractSliceDetail } from '@classes/contract-slice-detail';
import { Site } from '@classes/site';
import { ContractConcessionService } from 'app/core/services/contract-concession.service';
import { ContractSliceService } from 'app/core/services/contract-slice.service';
import { ContractService } from 'app/core/services/contract.service';
import { IndexService } from 'app/core/services/index.service';
import { UiService } from 'app/core/services/ui.service';
import { ContractConcessionComponent } from '../contract-concession/contract-concession.component';
import { ContractSliceComponent } from '../contract-slice/contract-slice.component';
import { MessageBoxParameter } from '@classes/message-box-parameter';
import { TranslateService } from '@ngx-translate/core';
import { MessageBox } from '@components/message-box/message-box.component';
import { NameToContractType, ContractType as ContractTypeEnum, ContractType } from '@enums/contract-type.enum';
import { Role } from '@enums/role.enum';

@Component({
  selector: 'app-contract-detail',
  templateUrl: './contract-detail.component.html',
  styleUrls: ['./contract-detail.component.scss'],
  styles: [`
  >>> app-contract-detail {
    width: 100%;
    display: flex;
    flex-direction: column;
  }
`]
})
export class ContractDetailComponent implements OnInit {

  contractForm: FormGroup;
  contract?: Contract | null;
  companyDetail?: CompanyDetail;
  contractDetail: ContractDetail;
  currentIndex?: Index | null;
  masterIndex?: Index | null;
  currentCoef?: number | null;

  ContractType = ContractTypeEnum;
  Role = Role;

  @Input() set companyDetail_(companyDetail: CompanyDetail) {
    this.companyDetail = companyDetail;
    let contract: Contract | undefined;
    if (this.companyDetail) {

      if (this.companyDetail.selectedContractID) {
        contract = (this.companyDetail?.company?.contracts || [])?.find(f => f.id == this.companyDetail?.selectedContractID)
      } else if (this.contractForm.controls.site.value) {
        contract = (this.companyDetail?.company?.contracts || [])?.find(f => f.siteId == this.contractForm.controls.site.value.id)
      }


      if (this.companyDetail.isCreationMode) {
        this.contractDetail.isCreationMode = false;
        this.contractDetail.isEditMode = false;
        this.contractDetail.isReadOnlyMode = false;
      } else {
        this.contractDetail.isReadOnlyMode = contract != null;
      }

      if (contract) {
        this.contract = contract;
        this.getContract(contract.id || "");
        this.getContractConcessions(contract.id || "");
        this.getContractSlices(contract.id || "");
      }
      else {
        this.contract = null;
        this.dataSourceContractSlices.data = [];
        this.dataSourceContractConcessions.data = [];
        this.initFormBuilder(new Contract({
          site: new Site(this.contractForm.controls.site.value)
        }));
      }


    }
  };

  @Output() refreshEmit = new EventEmitter<any>();
  @Output() backToCustomerEmit = new EventEmitter<any>();

  dataSourceContractSlices = new MatTableDataSource<ContractSlice>();
  displayedColumnsSlice: string[] = [
    'index',
    'price',
    'priceIndex',
    'tonnage',
    'action'
  ];
  dataSourceContractConcessions = new MatTableDataSource<ContractConcession>();
  displayedColumnsConcession: string[] = [
    'area',
    'price',
    'priceIndex',
    'percentageDiscount',
    'zone',
    'isFittedOutArea',
    'isGoodAccessibility',
    'startDate',
    'action'
  ];

  constructor(
    private formBuilder: FormBuilder,
    public uiService: UiService,
    private contractService: ContractService,
    private contractSliceService: ContractSliceService,
    private contractConcessionService: ContractConcessionService,
    public dialog: MatDialog,
    private indexService: IndexService,
    public translateService: TranslateService
  ) { }

  ngOnInit() {
    this.contractDetail = new ContractDetail();
    this.initFormBuilder();
    this.indexService.currentIndex.subscribe(value => {
      this.currentIndex = value;
    });
    this.indexService.masterIndex.subscribe(value => {
      this.masterIndex = value;
    });
    this.indexService.currentCoef.subscribe(value => {
      this.currentCoef = value;
    });
  }

  private initFormBuilder(value?: Contract | null) {
    var editMode = !(this.contractDetail?.isEditMode || this.contractDetail?.isCreationMode);
    const fb = this.formBuilder;
    this.contractForm = fb.group({
      id: fb.control(value?.id),
      site: fb.control({ value: value?.site, disabled: false }, [Validators.required]),
      comment: fb.control({ value: value?.comment, disabled: editMode }, []),
      dateBegin: fb.control({ value: value?.dateBegin, disabled: editMode },
        ((value?.contractTypeEnum == ContractType.publicDock) && (value?.isPunctualHandling))
          ? []
          : []),
      dateEnd: fb.control({ value: value?.dateEnd, disabled: editMode },
        ((value?.contractTypeEnum == ContractType.publicDock) && (value?.isPunctualHandling))
          ? []
          : []),
      dateStartBilling: fb.control({ value: value?.dateStartBilling, disabled: editMode },
        ((value?.contractTypeEnum == ContractType.publicDock) && (value?.isPunctualHandling))
          ? []
          : []),
      quantityMin: fb.control({ value: value?.quantityMin, disabled: editMode },
        ((value?.contractTypeEnum == ContractType.publicDock) && (value?.isPunctualHandling))
          ? []
          : [Validators.required]
      ),
      sliceDegressiveness: fb.control({ value: value?.sliceDegressiveness, disabled: editMode }, []),
      billingName: fb.control({ value: value?.billingName, disabled: editMode }, []),
      billingEmail: fb.control({ value: value?.billingEmail, disabled: editMode }, []),
      billingAddress: fb.group({
        id: fb.control(value?.billingAddress?.id),
        address1: fb.control({ value: value?.billingAddress?.address1, disabled: editMode }, []),
        city: fb.control({ value: value?.billingAddress?.city, disabled: editMode }, []),
        zip: fb.control({ value: value?.billingAddress?.zip, disabled: editMode }, []),
      }),

    });

    this.contractForm.controls.site.valueChanges.subscribe(value => {
      if (value && typeof value == "object" && this.companyDetail?.company?.contracts) {
        const contract = (this.companyDetail?.company?.contracts || [])?.find(f => f.siteId == this.contractForm.controls.site.value.id)
        if (contract) {
          this.contract = contract;
          this.contractDetail.isCreationMode = false;
          this.contractDetail.isReadOnlyMode = true;
          this.contractDetail.isEditMode = false;
          this.getContract(contract.id || "");
          this.getContractConcessions(contract.id || "");
          this.getContractSlices(contract.id || "");
        }
        else {
          this.contract = null;
          this.contractDetail.contract = null;
          this.contractDetail.isCreationMode = true;
          this.contractDetail.isReadOnlyMode = false;
          this.contractDetail.isEditMode = false;
          this.dataSourceContractSlices.data = [];
          this.dataSourceContractConcessions.data = [];
          this.initFormBuilder(new Contract({
            site: new Site(this.contractForm.controls.site.value)
          }));
        }
      }
    });

  }

  onSubmit() {
    const contract = new Contract(
      this.contractForm.value
    );
    contract.companyId = this.companyDetail?.id || this.companyDetail?.company?.id;
    // return false;
    this.uiService.addRouteLoading();
    this.contractService.createOrUpdateContract(contract).subscribe(data => {
      this.contractDetail.isEditMode = false;
      this.contractDetail.isCreationMode = false;
      this.contractDetail.isReadOnlyMode = true;
      this.contractDetail.contract = data;
      this.contract = data;
      this.refreshEmit.emit();
      this.initFormBuilder(data);
      this.uiService.displayMessageToast('contract.success-edit');
      this.uiService.removeRouteLoading();
    }, error => {
      console.error(error);
      this.uiService.removeRouteLoading();
    })

  }
  onDelete() {
    if (this.contract && this.contract?.id) {
      const messageBoxParameter: MessageBoxParameter = {
        message: this.translateService.instant('contract.confirm-delete'),
        dialog: this.dialog,
        title: 'Confirmation',
        buttons: ['yes', 'no'],
        width: "400px"
      };


      MessageBox.show(messageBoxParameter).subscribe(mbResult => {
        if (mbResult && mbResult.value) {
          this.uiService.addRouteLoading();
          this.contractService.deleteContract(this.contract?.id || "").subscribe(data => {
            this.uiService.displayMessageToast('contract.success-delete');
            this.contractDetail.isEditMode = false;
            this.contractDetail.isReadOnlyMode = true;
            this.dataSourceContractSlices.data = data || [];
            this.dataSourceContractConcessions.data = data || [];
            this.refreshEmit.emit();
            this.initFormBuilder(new Contract({
              site: new Site(this.contractForm.controls.site.value)
            }));
            this.uiService.removeRouteLoading();
            this.backToCustomerEmit.emit();
          }, error => {
            console.error(error);
            this.uiService.removeRouteLoading();
          });
        }
      });
    }

  }
  onEdit() {
    this.contractDetail.isEditMode = true;
    this.contractDetail.isReadOnlyMode = false;
    this.initFormBuilder(this.contractDetail.contract);
  }
  onCancel() {
    this.contractDetail.isEditMode = false;
    this.contractDetail.isReadOnlyMode = true;
    if (this.contract?.id) {
      this.uiService.addRouteLoading();
      this.contractService.getContract(this.contract?.id).subscribe(contract => {
        this.initFormBuilder(contract);
        this.uiService.removeRouteLoading();
      }, error => {
        console.error(error);
        this.uiService.removeRouteLoading();
      })
    }
  }


  getContract(contractId: string) {
    this.uiService.addRouteLoading();
    this.contractService.getContract(contractId).subscribe(data => {
      this.contractDetail.contract = data;
      this.contractDetail.contract.contractTypeEnum = NameToContractType.get(data.contractType?.name || "");
      this.initFormBuilder(data);
      this.uiService.removeRouteLoading();
    }, error => {
      console.error(error);
      this.uiService.removeRouteLoading();
    });

  }
  getContractSlices(contractId: string) {
    this.uiService.addRouteLoading();
    this.contractSliceService.getContractSlicesByContractID(contractId).subscribe(data => {
      data.forEach(f => {
        f.priceIndex = (f.price || 1) * (this.indexService.currentCoef?.value || 1);
        if (f.isLastSlice) {
          f.tonnage = Math.max(...data.map(o => (o.tonnage || 0)));
        }
      })
      this.dataSourceContractSlices.data = (data || [])
        .sort((a, b) => ((a.index || 0) > (b.index || 0)) ? 1 : (((b.index || 0) > (a.index || 0)) ? -1 : 0));
      this.uiService.removeRouteLoading();
    }, error => {
      console.error(error);
      this.uiService.removeRouteLoading();
    });
  }
  getContractConcessions(contractId: string) {
    this.uiService.addRouteLoading();
    this.contractConcessionService.getContractConcessionsByContractID(contractId).subscribe(data => {
      data.forEach(f => {
        f.priceIndex = (f.price || 1) * (this.indexService.currentCoef?.value || 1);
      })
      this.dataSourceContractConcessions.data = (data || [])
        .sort((a, b) => ((a.area || 0) > (b.area || 0)) ? 1 : (((b.area || 0) > (a.area || 0)) ? -1 : 0));
      this.uiService.removeRouteLoading();
    }, error => {
      console.error(error);
      this.uiService.removeRouteLoading();
    });
  }

  onCreateSlice() {
    const data = new ContractSliceDetail({
      isCreationMode: true,
      contract: this.contractDetail.contract
    });
    const dialogRef = this.dialog.open(ContractSliceComponent, {
      data,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(value => {
      if (value) {
        this.getContractSlices(this.contract?.id || "");
      }
    })
  }
  onCreateConcessions() {
    const data = new ContractConcessionDetail({
      isCreationMode: true,
      contract: this.contractDetail.contract
    });
    const dialogRef = this.dialog.open(ContractConcessionComponent, {
      data,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(value => {
      if (value) {
        this.getContractConcessions(this.contract?.id || "");
      }
    })
  }

  onEditSlice(contractSlice: ContractSlice) {
    const data = new ContractSliceDetail({
      contractSlice: contractSlice,
      contractSlices: this.dataSourceContractSlices.data,
      contract: this.contract,
      isEditMode: true
    });
    const dialogRef = this.dialog.open(ContractSliceComponent, {
      data,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(value => {
      if (value) {
        this.getContractSlices(this.contract?.id || "");
      }
    })
  }
  onDeleteSlice(contractSlice: ContractSlice) {
    const messageBoxParameter: MessageBoxParameter = {
      message: this.translateService.instant('contract-slice.confirm-delete'),
      dialog: this.dialog,
      title: 'Confirmation',
      buttons: ['yes', 'no'],
      width: "400px"
    };

    MessageBox.show(messageBoxParameter).subscribe(mbResult => {
      if (mbResult && mbResult.value) {
        this.uiService.addRouteLoading();
        this.contractSliceService.deleteContractSlice(contractSlice?.id || "").subscribe(data => {
          this.uiService.removeRouteLoading();
          this.uiService.displayMessageToast('contract-slice.success-delete');
          this.getContractSlices(this.contract?.id || "")
        }, error => {
          console.error(error);
          this.uiService.removeRouteLoading();
        });
      }
    });
  }

  onEditConcession(contractConcession: ContractConcession) {
    const data = new ContractConcessionDetail({
      contractConcession: contractConcession,
      contract: this.contract,
      isEditMode: true
    });
    const dialogRef = this.dialog.open(ContractConcessionComponent, {
      data,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(value => {
      if (value) {
        this.getContractConcessions(this.contract?.id || "");
      }
    })
  }

  onDeleteConcession(contractConcession: ContractConcession) {

    const messageBoxParameter: MessageBoxParameter = {
      message: this.translateService.instant('contract-concession.confirm-delete'),
      dialog: this.dialog,
      title: 'Confirmation',
      buttons: ['yes', 'no'],
      width: "400px"
    };

    MessageBox.show(messageBoxParameter).subscribe(mbResult => {
      if (mbResult && mbResult.value) {
        this.uiService.addRouteLoading();
        this.contractConcessionService.deleteContractConcession(contractConcession?.id || "").subscribe(data => {
          this.uiService.removeRouteLoading();
          this.uiService.displayMessageToast('contract-concession.success-delete');
          this.getContractConcessions(this.contract?.id || "")
        }, error => {
          console.error(error);
          this.uiService.removeRouteLoading();
        });
      }
    });
  }

  isRequiredField(field: string) {
    const form_field = <AbstractControl>this.contractForm.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return (validator && validator.required);
  }
}
